import React, { useState, useEffect, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Shield, Settings, Layout, UserCog, LayoutGrid, RefreshCw, ChevronDown, ChevronRight, CheckCircle, Check, PanelLeft } from 'lucide-react';
import { OrganizationRolesManager } from './OrganizationRolesManager';
import FeatureCapabilitiesPage from '@/src/app/admin/feature-capabilities/page';
import { PageCapabilitiesManagement } from './PageCapabilitiesManagement';
import { RolePageAccessManagement } from './RolePageAccessManagement';
import { NavigationLayoutAdmin } from './NavigationLayoutAdmin';
import { apiClient } from '@/services/apiClient';

interface UnifiedAccessControlProps {
  organizationId: number;
  organizationName: string;
}

// Category display labels
const CATEGORY_LABELS: Record<string, string> = {
  core: 'Core', member_management: 'Member Management', enrollments: 'Enrollments',
  care_coordination: 'Care Coordination', communications: 'Communications',
  analytics: 'Analytics & Reporting', billing: 'Billing & Financials',
  incentives: 'Incentives & Rewards', programs: 'Programs', workflows: 'Workflows',
  team: 'Team', audits: 'Audits & Compliance', administration: 'Administration',
  integrations: 'Integrations', employer: 'Employer Platform',
  employee_portal: 'Employee Portal', medicaid: 'Medicaid', weavein: 'Marketing & Sales',
  workforce: 'Training & Workforce',
};

// Wizard step definitions
const WIZARD_STEPS = [
  { key: 'roles', label: 'Roles', description: 'Define organization roles', icon: Shield },
  { key: 'features', label: 'Features', description: 'Enable platform features', icon: Settings },
  { key: 'pages', label: 'Pages', description: 'Configure page visibility', icon: Layout },
  { key: 'navigation', label: 'Navigation', description: 'Arrange sidebar layout', icon: PanelLeft },
  { key: 'permissions', label: 'Permissions', description: 'Set role-page access', icon: UserCog },
  { key: 'matrix', label: 'Matrix', description: 'Review permission matrix', icon: LayoutGrid },
] as const;

export function UnifiedAccessControl({ organizationId, organizationName }: UnifiedAccessControlProps) {
  const [activeTab, setActiveTab] = useState<string>('roles');
  const [permissionRoleFilter, setPermissionRoleFilter] = useState<string | null>(null);

  // Matrix data — fetched directly here so it loads when the Matrix tab is clicked
  const [matrixData, setMatrixData] = useState<any>(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [matrixSaving, setMatrixSaving] = useState<string | null>(null); // "roleType:pageKey" being saved
  const [openCellMenu, setOpenCellMenu] = useState<string | null>(null); // "roleType:pageKey" with menu open

  const fetchMatrix = async () => {
    if (!organizationId) return;
    setMatrixLoading(true);
    setMatrixError(null);
    try {
      const response = await apiClient.get(
        `/api/organizations/${organizationId}/role-permissions/matrix`,
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );
      setMatrixData(response.data);
    } catch (err: any) {
      setMatrixError(err.response?.data?.detail || err.message || 'Failed to load matrix');
      setMatrixData(null);
    } finally {
      setMatrixLoading(false);
    }
  };

  // Fetch matrix when tab is activated
  useEffect(() => {
    if (activeTab === 'matrix' && !matrixData && !matrixLoading) {
      fetchMatrix();
    }
  }, [activeTab]);

  // Set a single cell permission to a specific level
  const setCellPermission = async (roleType: string, pageKey: string, newLevel: string) => {
    setOpenCellMenu(null);
    const savingKey = `${roleType}:${pageKey}`;
    setMatrixSaving(savingKey);
    try {
      await apiClient.put(
        `/api/organizations/${organizationId}/role-permissions/${roleType}/${pageKey}`,
        { page_key: pageKey, access_level: newLevel },
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );
      await fetchMatrix();
    } catch (err: any) {
      setMatrixError(err.response?.data?.detail || 'Failed to update permission');
    } finally {
      setMatrixSaving(null);
    }
  };

  // Update all pages in a category for a specific role
  const updateCategoryForRole = async (roleType: string, category: string, accessLevel: 'enabled' | 'disabled' | 'inherit') => {
    setMatrixSaving(`${roleType}:${category}:bulk`);
    try {
      await apiClient.post(
        `/api/organizations/${organizationId}/role-permissions/${roleType}/category/${category}`,
        { access_level: accessLevel },
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );
      await fetchMatrix();
    } catch (err: any) {
      setMatrixError(err.response?.data?.detail || 'Failed to update category permissions');
    } finally {
      setMatrixSaving(null);
    }
  };

  const handleConfigurePermissions = (roleType: string) => {
    setPermissionRoleFilter(roleType);
    setActiveTab('permissions');
  };

  const handleTabChange = (tab: string) => {
    if (tab !== 'permissions') setPermissionRoleFilter(null);
    setActiveTab(tab);
  };

  // Group matrix rows by category
  const matrixByCategory = useMemo(() => {
    if (!matrixData?.matrix) return {};
    const grouped: Record<string, any[]> = {};
    for (const row of matrixData.matrix) {
      const cat = row.category || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(row);
    }
    return grouped;
  }, [matrixData]);

  const roles = matrixData?.roles || [];

  const activeStepIndex = WIZARD_STEPS.findIndex(s => s.key === activeTab);

  // Guidance descriptions per step
  const stepGuidance: Record<string, string> = {
    roles: 'Start by defining the roles your organization needs. Each role determines what level of access members have across the platform.',
    features: 'Enable or disable platform-level feature flags. Disabled features will be hidden from all users regardless of their role.',
    pages: 'Control which pages are visible at the organization level. Pages disabled here cannot be accessed by any role.',
    navigation: 'Arrange how visible pages are grouped and ordered in the sidebar. Layout reflects the same pages and capabilities configured in the other tabs.',
    permissions: 'Fine-tune which roles can access which pages. This is where you wire roles to specific page-level permissions.',
    matrix: 'Review the full permission matrix across all roles and pages at a glance. Use bulk actions to quickly configure access by category.',
  };

  return (
    <div className="space-y-5">
      {/* ── Wizard Step Indicator ── */}
      <div className="overflow-hidden rounded-[var(--v2-r-lg)] border border-[var(--v2-border)] bg-[var(--v2-surface)] shadow-[var(--v2-shadow-1)]">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            {WIZARD_STEPS.map((step, idx) => {
              const StepIcon = step.icon;
              const isActive = idx === activeStepIndex;
              const isCompleted = idx < activeStepIndex;

              return (
                <React.Fragment key={step.key}>
                  {/* Step node */}
                  <button
                    onClick={() => handleTabChange(step.key)}
                    className="flex flex-col items-center gap-1.5 group flex-shrink-0"
                  >
                    <div
                      className={`flex items-center justify-center w-9 h-9 rounded-full border-2 transition-all ${
                        isActive
                          ? 'border-[var(--v2-accent)] bg-[var(--v2-accent)] text-white'
                          : isCompleted
                            ? 'border-emerald-500 bg-emerald-500 text-white'
                            : 'border-[var(--v2-border-muted)] bg-[var(--v2-paper)] text-[var(--v2-ink-400)] group-hover:border-[var(--v2-accent)] group-hover:text-[var(--v2-accent)]'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <StepIcon className="w-4 h-4" />
                      )}
                    </div>
                    <span
                      className={`text-[11px] font-display font-medium transition-colors ${
                        isActive
                          ? 'text-[var(--v2-accent)]'
                          : isCompleted
                            ? 'text-emerald-600'
                            : 'text-[var(--v2-ink-400)] group-hover:text-[var(--v2-ink-500)]'
                      }`}
                    >
                      {step.label}
                    </span>
                    <span className="text-[10px] text-[var(--v2-ink-400)] hidden sm:block max-w-[110px] text-center leading-tight">
                      {step.description}
                    </span>
                  </button>

                  {/* Connector line */}
                  {idx < WIZARD_STEPS.length - 1 && (
                    <div className="flex-1 mx-2 mt-[-18px] sm:mt-[-28px]">
                      <div
                        className={`h-0.5 w-full rounded-full transition-colors ${
                          idx < activeStepIndex
                            ? 'bg-emerald-400'
                            : 'bg-[var(--v2-border-muted)]'
                        }`}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Guidance Card ── */}
      <div className="rounded-[var(--v2-r)] border border-[var(--v2-accent)]/20 bg-[var(--v2-accent)]/5 px-4 py-3">
        <p className="text-[13px] text-[var(--v2-ink-500)]">
          <span className="font-display font-semibold text-[var(--v2-accent)] mr-1.5">
            Step {activeStepIndex + 1}:
          </span>
          {stepGuidance[activeTab] || ''}
        </p>
      </div>

      {/* ── Tabbed Content ── */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="flex flex-wrap gap-1 bg-transparent border-b border-[var(--v2-border)] rounded-none p-0 h-auto">
          {WIZARD_STEPS.map((step) => {
            const StepIcon = step.icon;
            return (
              <TabsTrigger
                key={step.key}
                value={step.key}
                className="relative flex items-center gap-1.5 px-3 py-2 text-[13px] font-display font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--v2-accent)] data-[state=active]:text-[var(--v2-accent)] data-[state=active]:bg-transparent data-[state=active]:shadow-none text-[var(--v2-ink-400)] hover:text-[var(--v2-ink)] transition-colors bg-transparent"
              >
                <StepIcon className="h-3.5 w-3.5" />
                {step.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="roles" className="mt-4">
          <div className="overflow-hidden rounded-[var(--v2-r-lg)] border border-[var(--v2-border)] bg-[var(--v2-surface)]">
            <OrganizationRolesManager
              organizationId={organizationId}
              organizationName={organizationName}
              onConfigurePermissions={handleConfigurePermissions}
            />
          </div>
        </TabsContent>

        <TabsContent value="features" className="mt-4">
          <div className="overflow-hidden rounded-[var(--v2-r-lg)] border border-[var(--v2-border)] bg-[var(--v2-surface)]">
            <FeatureCapabilitiesPage
              organizationId={organizationId}
              organizationName={organizationName}
            />
          </div>
        </TabsContent>

        <TabsContent value="pages" className="mt-4">
          <div className="overflow-hidden rounded-[var(--v2-r-lg)] border border-[var(--v2-border)] bg-[var(--v2-surface)]">
            <PageCapabilitiesManagement organizationId={organizationId} organizationName={organizationName} />
          </div>
        </TabsContent>

        <TabsContent value="navigation" className="mt-4">
          <div className="overflow-hidden rounded-[var(--v2-r-lg)] border border-[var(--v2-border)] bg-[var(--v2-surface)] p-4">
            <NavigationLayoutAdmin organizationId={organizationId} organizationName={organizationName} />
          </div>
        </TabsContent>

        <TabsContent value="permissions" className="mt-4">
          <div className="overflow-hidden rounded-[var(--v2-r-lg)] border border-[var(--v2-border)] bg-[var(--v2-surface)]">
            <RolePageAccessManagement
              organizationId={organizationId}
              organizationName={organizationName}
              initialRoleFilter={permissionRoleFilter}
            />
          </div>
        </TabsContent>

        <TabsContent value="matrix" className="mt-4">
          <div className="overflow-hidden rounded-[var(--v2-r-lg)] border border-[var(--v2-border)] bg-[var(--v2-surface)]">
            {/* Matrix header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--v2-border)]">
              <div>
                <h3 className="font-display text-[15px] font-semibold text-[var(--v2-ink)] flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4 text-[var(--v2-accent)]" />
                  Permission Matrix
                </h3>
                <p className="text-[13px] text-[var(--v2-ink-400)] mt-0.5">
                  All roles side-by-side — see who can access what at a glance
                </p>
              </div>
              <button
                onClick={fetchMatrix}
                disabled={matrixLoading}
                className="p-2 text-[var(--v2-ink-400)] hover:text-[var(--v2-ink)] hover:bg-[var(--v2-paper-2)] rounded-[var(--v2-r)] transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${matrixLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Matrix body */}
            <div className="px-5 py-4">
              {matrixLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-[var(--v2-accent)]/20 border-t-[var(--v2-accent)] rounded-full animate-spin" />
                </div>
              ) : matrixError ? (
                <div className="rounded-[var(--v2-r)] border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4 text-red-700 dark:text-red-300 text-[13px]">
                  {matrixError}
                </div>
              ) : roles.length === 0 ? (
                <div className="text-center py-12 text-[var(--v2-ink-400)]">
                  <p className="text-[13px]">No non-admin roles configured for this organization.</p>
                  <p className="text-[11px] mt-1">Super admin roles have full access and are not shown in the matrix.</p>
                </div>
              ) : (
                // TRUS-562: the table scrolls inside its own box so the role names in
                // the header stay visible however far down the page list you go.
                <div className="overflow-auto max-h-[70vh]">
                  <table className="w-full text-[11px] border-collapse min-w-[600px]">
                    <thead>
                      <tr className="bg-[var(--v2-paper-2)]">
                        <th className="text-left px-3 py-3 font-display font-medium text-[var(--v2-ink-500)] border-b border-[var(--v2-border)] sticky left-0 top-0 bg-[var(--v2-paper-2)] z-30 min-w-[200px]">
                          Page
                        </th>
                        {roles.map((role: any) => {
                          const roleType = typeof role === 'string' ? role : role.type;
                          const roleName = typeof role === 'string' ? role : (role.display_name || role.type);
                          const systemRole = typeof role === 'string' ? null : role.system_role;
                          return (
                            <th key={roleType} className="text-center px-2 py-3 font-display font-medium text-[var(--v2-ink-500)] border-b border-[var(--v2-border)] min-w-[90px] sticky top-0 z-20 bg-[var(--v2-paper-2)]">
                              <div className="flex flex-col items-center gap-1">
                                <span className="truncate max-w-[80px]">{roleName}</span>
                                {systemRole && (
                                  <span className="text-[10px] font-mono px-1 py-0.5 rounded-[var(--v2-r)] bg-[var(--v2-paper)] text-[var(--v2-ink-400)]">
                                    {systemRole}
                                  </span>
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(matrixByCategory).map(([category, rows]) => {
                        const expanded = expandedCategories[category] !== false; // default expanded
                        const accessibleByRole: Record<string, number> = {};
                        roles.forEach((role: any) => {
                          const rt = typeof role === 'string' ? role : role.type;
                          accessibleByRole[rt] = rows.filter((r: any) => r.roles?.[rt]?.effective_access).length;
                        });

                        return (
                          <React.Fragment key={category}>
                            <tr className="hover:bg-[var(--v2-paper)]/50">
                              <td className="px-3 py-2 bg-[var(--v2-paper-2)] font-display font-semibold text-[13px] text-[var(--v2-ink)] border-b border-[var(--v2-border)] sticky left-0 z-10">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => setExpandedCategories(prev => ({ ...prev, [category]: !expanded }))} className="flex items-center gap-1">
                                    {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                    {CATEGORY_LABELS[category] || category}
                                  </button>
                                  <span className="text-[11px] font-normal text-[var(--v2-ink-400)]">({rows.length})</span>
                                </div>
                              </td>
                              {roles.map((role: any) => {
                                const rt = typeof role === 'string' ? role : role.type;
                                const count = accessibleByRole[rt] || 0;
                                const pct = rows.length > 0 ? Math.round((count / rows.length) * 100) : 0;
                                const isBulkSaving = matrixSaving === `${rt}:${category}:bulk`;
                                return (
                                  <td key={rt} className="px-1 py-1 text-center bg-[var(--v2-paper-2)] border-b border-[var(--v2-border)]">
                                    <div className="flex flex-col items-center gap-0.5">
                                      <span className={`text-[11px] font-mono font-medium ${pct === 100 ? 'text-emerald-600' : pct === 0 ? 'text-red-500' : 'text-amber-600'}`}>
                                        {count}/{rows.length}
                                      </span>
                                      <div className={`flex gap-0.5 ${isBulkSaving ? 'animate-pulse' : ''}`}>
                                        <button
                                          onClick={() => updateCategoryForRole(rt, category, 'enabled')}
                                          disabled={!!matrixSaving}
                                          className="px-1 py-0.5 text-[9px] font-medium rounded-[var(--v2-r)] bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/60 transition-colors"
                                          title={`Allow all ${CATEGORY_LABELS[category] || category} pages for this role`}
                                        >
                                          All
                                        </button>
                                        <button
                                          onClick={() => updateCategoryForRole(rt, category, 'disabled')}
                                          disabled={!!matrixSaving}
                                          className="px-1 py-0.5 text-[9px] font-medium rounded-[var(--v2-r)] bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
                                          title={`Deny all ${CATEGORY_LABELS[category] || category} pages for this role`}
                                        >
                                          None
                                        </button>
                                        <button
                                          onClick={() => updateCategoryForRole(rt, category, 'inherit')}
                                          disabled={!!matrixSaving}
                                          className="px-1 py-0.5 text-[9px] font-medium rounded-[var(--v2-r)] bg-[var(--v2-paper)] text-[var(--v2-ink-400)] hover:bg-[var(--v2-paper-2)] transition-colors"
                                          title={`Reset all ${CATEGORY_LABELS[category] || category} pages to default for this role`}
                                        >
                                          Def
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                            {expanded && rows.map((row: any) => (
                              <tr key={row.page_key} className="border-b border-[var(--v2-border-muted)] hover:bg-[var(--v2-paper)]/50">
                                <td className="px-3 py-1.5 pl-8 text-[var(--v2-ink)] sticky left-0 bg-[var(--v2-surface)] z-10">
                                  <div className="flex items-center gap-1">
                                    <span className="text-[13px]">{row.page_name}</span>
                                    {!row.org_enabled && row.parent_capability_enabled === false && (
                                      <span className="text-[10px] font-mono px-1 py-0.5 rounded-[var(--v2-r)] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400" title="Disabled because parent capability is turned off">CAP OFF</span>
                                    )}
                                    {!row.org_enabled && row.parent_capability_enabled !== false && (
                                      <span className="text-[10px] font-mono px-1 py-0.5 rounded-[var(--v2-r)] bg-[var(--v2-paper-2)] text-[var(--v2-ink-400)]">OFF</span>
                                    )}
                                  </div>
                                </td>
                                {roles.map((role: any) => {
                                  const rt = typeof role === 'string' ? role : role.type;
                                  const entry = row.roles?.[rt];
                                  if (!entry) return <td key={rt} className="px-1 py-1.5 text-center"><span className="text-[var(--v2-ink-400)]">—</span></td>;

                                  const bgColor = !row.org_enabled ? 'bg-[var(--v2-paper-2)]'
                                    : entry.effective_access
                                      ? entry.access_level === 'enabled' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-emerald-50 dark:bg-emerald-900/10'
                                    : entry.access_level === 'disabled' ? 'bg-red-100 dark:bg-red-900/30'
                                    : entry.is_excluded_by_default ? 'bg-orange-50 dark:bg-orange-900/10'
                                    : 'bg-red-50 dark:bg-red-900/10';

                                  const textColor = !row.org_enabled ? 'text-[var(--v2-ink-400)]'
                                    : entry.effective_access ? 'text-emerald-700 dark:text-emerald-400'
                                    : 'text-red-600 dark:text-red-400';

                                  const symbol = !row.org_enabled ? '—'
                                    : entry.access_level === 'enabled' ? '✓'
                                    : entry.access_level === 'disabled' ? '✗'
                                    : entry.effective_access ? '·'
                                    : entry.is_excluded_by_default ? '⊘' : '·';

                                  const isSaving = matrixSaving === `${rt}:${row.page_key}`;
                                  const cellKey = `${rt}:${row.page_key}`;
                                  const isMenuOpen = openCellMenu === cellKey;

                                  return (
                                    <td key={rt} className="px-1 py-1 text-center relative">
                                      <button
                                        onClick={() => !row.org_enabled ? null : setOpenCellMenu(isMenuOpen ? null : cellKey)}
                                        disabled={!row.org_enabled || !!matrixSaving}
                                        className={`inline-flex items-center justify-center w-6 h-6 rounded-[var(--v2-r)] text-[11px] font-bold transition-all ${bgColor} ${textColor} ${
                                          !row.org_enabled ? 'cursor-not-allowed' : 'cursor-pointer hover:ring-2 hover:ring-[var(--v2-accent)] hover:ring-offset-1'
                                        } ${isSaving ? 'animate-pulse' : ''} ${isMenuOpen ? 'ring-2 ring-[var(--v2-accent)]' : ''}`}
                                        title={!row.org_enabled ? 'Disabled at org level' : 'Click to change permission'}
                                      >
                                        {symbol}
                                      </button>
                                      {isMenuOpen && (
                                        <>
                                          <div className="fixed inset-0 z-20" onClick={() => setOpenCellMenu(null)} />
                                          <div className="absolute z-30 top-full left-1/2 -translate-x-1/2 mt-1 bg-[var(--v2-surface)] border border-[var(--v2-border)] rounded-[var(--v2-r-lg)] shadow-[var(--v2-shadow-1)] py-1 min-w-[120px]">
                                            <button
                                              onClick={() => setCellPermission(rt, row.page_key, 'enabled')}
                                              className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-[var(--v2-paper)] ${entry.access_level === 'enabled' ? 'font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20' : 'text-[var(--v2-ink)]'}`}
                                            >
                                              <span className="w-4 h-4 inline-flex items-center justify-center rounded-[var(--v2-r)] bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 text-[10px] font-bold">✓</span>
                                              Allow
                                            </button>
                                            <button
                                              onClick={() => setCellPermission(rt, row.page_key, 'disabled')}
                                              className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-[var(--v2-paper)] ${entry.access_level === 'disabled' ? 'font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20' : 'text-[var(--v2-ink)]'}`}
                                            >
                                              <span className="w-4 h-4 inline-flex items-center justify-center rounded-[var(--v2-r)] bg-red-100 dark:bg-red-900/30 text-red-600 text-[10px] font-bold">✗</span>
                                              Deny
                                            </button>
                                            <div className="border-t border-[var(--v2-border-muted)] my-1" />
                                            <button
                                              onClick={() => setCellPermission(rt, row.page_key, 'inherit')}
                                              className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-[var(--v2-paper)] ${entry.access_level === 'inherit' ? 'font-bold text-[var(--v2-ink)] bg-[var(--v2-paper)]' : 'text-[var(--v2-ink)]'}`}
                                            >
                                              <span className="w-4 h-4 inline-flex items-center justify-center rounded-[var(--v2-r)] bg-[var(--v2-paper-2)] text-[var(--v2-ink-400)] text-[10px] font-bold">·</span>
                                              Default
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Legend */}
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] text-[var(--v2-ink-400)] border-t border-[var(--v2-border)] pt-3">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-[var(--v2-r)] bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 text-[11px] font-bold">✓</span>
                      <span>Allowed</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-[var(--v2-r)] bg-red-100 dark:bg-red-900/30 text-red-600 text-[11px] font-bold">✗</span>
                      <span>Denied</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-[var(--v2-r)] bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 text-[11px] font-bold">·</span>
                      <span>Inherited (accessible)</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-[var(--v2-r)] bg-orange-50 dark:bg-orange-900/10 text-red-600 text-[11px] font-bold">⊘</span>
                      <span>Excluded by default</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-[var(--v2-r)] bg-[var(--v2-paper-2)] text-[var(--v2-ink-400)] text-[11px]">—</span>
                      <span>Org disabled</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
